<template>
  <v-container>
    <v-overlay :value="loading">
      <v-progress-circular
        indeterminate
        size="64"
      />
    </v-overlay>
    <v-card
      width="100%"
      style="border-radius: 10px"
      class="pa-3"
      v-show="!loading"
    >
      <v-card-title class="title font-weight-light">
        <h3>Company Management</h3>
        <v-spacer />
        <v-text-field
          v-model="search"
          append-icon="mdi-magnify"
          label="Search"
          single-line
          hide-details
        />
      </v-card-title>
      <v-card-text>
        <v-data-table
          :headers="headers"
          :items="companiesFiltered"
          item-key="clientid"
          class="elevation-1"
          :footer-props="footerProps"
          @update:options="updateOptions"
          :options.sync="paginationOptions"
        >
          <template #item="{ item }">
            <tr>
              <td>{{ item.clientid }}</td>
              <td>{{ item.tradingname }}</td>
              <td>
                <v-chip
                  color="green"
                  dark
                  v-if="item.active"
                >
                  Active
                </v-chip>
                <v-chip
                  color="red"
                  dark
                  v-else
                >
                  Inactive
                </v-chip>
              </td>
              <td>
                <v-icon
                  color="green"
                  dark
                  v-if="isLinked(item.clientid)"
                >
                  mdi-check
                </v-icon>
              </td>
              <td>
                <v-btn
                  icon
                  small
                  color="green"
                  @click="handleEdit(item)"
                  v-if="isLinked(item.clientid)"
                  title="Edit"
                >
                  <v-icon small>
                    mdi-pencil
                  </v-icon>
                </v-btn>
                <v-btn
                  icon
                  small
                  color="blue"
                  @click="handleEdit(item)"
                  v-if="!isLinked(item.clientid)"
                  title="Link to Carpooling"
                >
                  <v-icon small>
                    mdi-sync
                  </v-icon>
                </v-btn>
              </td>
            </tr>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
    <v-dialog
      v-model="dialog_form"
      max-width="900"
      persistent
      scrollable
    >
      <v-card>
        <v-card-title>
          <span class="headline">
            Company
          </span>
        </v-card-title>
        <v-card-text>
          <v-container>
            <v-row>
              <v-col cols="12">
                <v-text-field
                  v-model="selectedCompany.clientid"
                  label="Client ID"
                  outlined
                  required
                  disabled
                  hide-details
                />
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model="selectedCompany.name"
                  label="Company Name"
                  outlined
                  required
                  disabled
                  hide-details
                />
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model="selectedCompany.metadata.company_email_address"
                  label="Company Email Address"
                  outlined
                  hide-details
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col sm="12">
                <v-divider />
              </v-col>
              <v-col
                sm="12"
                class="font-weight-medium"
              >
                Booking Configuration
              </v-col>
              <v-col cols="12">
                <v-checkbox
                  v-model="selectedCompany.metadata.overnight_booking"
                  label="Allow Overnight Booking"
                  hide-details
                  color="success"
                  class="mt-0"
                />
              </v-col>
              <v-col cols="12">
                <v-checkbox
                  v-model="selectedCompany.metadata.auto_approve_overnight_booking"
                  label="Auto Approve Overnight Booking"
                  hide-details
                  color="success"
                  class="mt-0"
                />
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model="selectedCompany.metadata.max_booking_days"
                  label="Maximum Booking Duration"
                  type="number"
                  suffix="days"
                  min="1"
                  persistent-hint
                  hint="Longest a single booking may run. Leave blank for no limit."
                />
              </v-col>
              <v-col cols="12">
                <v-checkbox
                  v-model="selectedCompany.metadata.recurring_booking"
                  label="Allow Recurring Booking"
                  hide-details
                  color="success"
                  class="mt-0"
                />
              </v-col>
              <v-col cols="12">
                <v-checkbox
                  v-model="selectedCompany.metadata.late_returning_key"
                  label="Send Notification of Late Returning Key"
                  hide-details
                  color="success"
                  class="mt-0"
                />
              </v-col>
              <v-col cols="12">
                <v-checkbox
                  v-model="selectedCompany.metadata.restrict_booking"
                  label="Restrict Booking Time"
                  hide-details
                  color="success"
                  class="mt-0"
                />
              </v-col>
              <v-col
                cols="12"
                v-if="selectedCompany.metadata.restrict_booking"
              >
                <v-row>
                  <v-col cols="12">
                    <v-select
                      v-model="selectedCompany.metadata.working_day"
                      :items="workingDays"
                      item-text="name"
                      item-value="value"
                      label="Working Day"
                      multiple
                      outlined
                      hide-details
                    />
                  </v-col>
                  <v-col cols="6">
                    <v-dialog
                      ref="dialog"
                      v-model="dialog_start"
                      :return-value.sync="selectedCompany.metadata.working_time.start"
                      persistent
                      width="290px"
                    >
                      <template #activator="{ on, attrs }">
                        <v-text-field
                          v-model="selectedCompany.metadata.working_time.start"
                          label="Start Working Time"
                          readonly
                          v-bind="attrs"
                          v-on="on"
                          outlined
                          hide-details
                        />
                      </template>
                      <v-time-picker
                        v-if="dialog_start"
                        v-model="selectedCompany.metadata.working_time.start"
                        full-width
                      >
                        <v-spacer />
                        <v-btn
                          text
                          color="primary"
                          @click="dialog_start = false"
                        >
                          Cancel
                        </v-btn>
                        <v-btn
                          text
                          color="primary"
                          @click="$refs.dialog.save(selectedCompany.metadata.working_time.start)"
                        >
                          OK
                        </v-btn>
                      </v-time-picker>
                    </v-dialog>
                  </v-col>
                  <v-col cols="6">
                    <v-dialog
                      ref="dialog2"
                      v-model="dialog_end"
                      :return-value.sync="selectedCompany.metadata.working_time.end"
                      persistent
                      width="290px"
                    >
                      <template #activator="{ on, attrs }">
                        <v-text-field
                          v-model="selectedCompany.metadata.working_time.end"
                          label="End Working Time"
                          readonly
                          v-bind="attrs"
                          v-on="on"
                          outlined
                          hide-details
                        />
                      </template>
                      <v-time-picker
                        v-if="dialog_end"
                        v-model="selectedCompany.metadata.working_time.end"
                        full-width
                      >
                        <v-spacer />
                        <v-btn
                          text
                          color="primary"
                          @click="dialog_end = false"
                        >
                          Cancel
                        </v-btn>
                        <v-btn
                          text
                          color="primary"
                          @click="$refs.dialog2.save(selectedCompany.metadata.working_time.end)"
                        >
                          OK
                        </v-btn>
                      </v-time-picker>
                    </v-dialog>
                  </v-col>
                </v-row>
              </v-col>
              <v-col
                cols="12"
                sm="6"
              >
                <v-select
                  v-model="selectedCompany.metadata.buffer_booking_time"
                  :items="bufferTimes"
                  label="Buffer Booking Time"
                  outlined
                  hide-details
                />
              </v-col>
              <v-col
                cols="12"
                sm="6"
              >
                <v-select
                  v-model="selectedCompany.metadata.minimum_booking_time"
                  :items="minimumTimes"
                  label="Minimum Booking Time"
                  outlined
                  hide-details
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col sm="12">
                <v-divider />
              </v-col>
              <v-col
                sm="12"
                class="font-weight-medium"
              >
                User Configuration
              </v-col>
              <v-col cols="12">
                <v-checkbox
                  v-model="selectedCompany.metadata.no_need_rfid"
                  label="Bypass RFID Requirement"
                  outlined
                  hide-details
                  color="success"
                />
              </v-col>
              <v-col cols="12">
                <v-checkbox
                  v-model="selectedCompany.metadata.no_need_destination"
                  label="Hide Popular Destination"
                  outlined
                  hide-details
                  color="success"
                />
              </v-col>
              <v-col cols="12">
                <v-checkbox
                  v-model="selectedCompany.metadata.no_need_search_address"
                  label="Hide Search Address Destination"
                  outlined
                  hide-details
                  color="success"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col sm="12">
                <v-divider />
              </v-col>
              <v-col
                sm="12"
                class="font-weight-medium"
              >
                Azure AD SSO
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model="selectedCompany.metadata.azure_jwks_uri"
                  label="Azure JWKS URI"
                  outlined
                  hide-details
                />
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model="selectedCompany.metadata.azure_tenant_id"
                  label="Azure Tenant ID"
                  outlined
                  hide-details
                />
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model="selectedCompany.metadata.azure_client_id"
                  label="Azure Client ID"
                  outlined
                  hide-details
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col sm="12">
                <v-divider />
              </v-col>
              <v-col
                sm="12"
                class="font-weight-medium"
              >
                Torus API Key
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model="selectedCompany.metadata.provisioning_key"
                  label="Provisioning Key"
                  outlined
                  hide-details
                />
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            color="error darken-1"
            @click="dialog_form = false"
          >
            Cancel
          </v-btn>
          <v-btn
            color="success darken-1"
            @click="handleSave"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
export default {
  data: () => ({
    loading: false,
    saving: false,
    dialog_form: false,
    dialog_start: false,
    dialog_end: false,
    companies: [],
    createdCompanies: [],
    search: '',
    defaultSelectedCompany: {
      name: '',
      clientid: '',
      metadata: {
        company_email_address: '',
        working_day: [],
        working_time: {
          start: '',
          end: '',
        },
        buffer_booking_time: '0',
        minimum_booking_time: '0',
        azure_jwks_uri: '',
        azure_tenant_id: '',
        azure_client_id: '',
        provisioning_key: '',
        restrict_booking: false,
        overnight_booking: false,
        auto_approve_overnight_booking: false,
        max_booking_days: '',
        recurring_booking: false,
        no_need_rfid: false,
        no_need_destination: false,
        no_need_search_address: false,
        late_returning_key: false,
      }
    },
    selectedCompany: {},
    workingDays: [
      { name: 'Sunday', value: 0 },
      { name: 'Monday', value: 1 },
      { name: 'Tuesday', value: 2 },
      { name: 'Wednesday', value: 3 },
      { name: 'Thursday', value: 4 },
      { name: 'Friday', value: 5 },
      { name: 'Saturday', value: 6 }
    ],
    bufferTimes: ["0", "5", "10", "15", "20", "25", "30", "35", "40", "45", "50", "55", "60"],
    minimumTimes: ["0", "5", "10", "15", "20", "25", "30", "35", "40", "45", "50", "55", "60"],
    footerProps: {
      itemsPerPageOptions: [2, 3, 5, 10, 20, 50, 100, 200, 500]
    },
    paginationOptions: {
      page: 1,
      itemsPerPage: 10,
      sortBy: [],
      sortDesc: []
    },
    headers: [
      { text: 'Client ID', align: 'start', value: 'clientid' },
      { text: 'Company Name', value: 'tradingname' },
      { text: 'Status', value: 'active' },
      { text: 'Link to Carpooling', value: 'clientid', sortable: false },
      { text: 'Action', value: 'action', sortable: false },
    ],
  }),
  created() {
    this.getCompany();
    this.getCreatedCompany();
  },
  mounted() {
    this.selectedCompany = {
      ...this.defaultSelectedCompany,
      metadata: this.defaultMetadata()
    };
  },
  computed: {
    companiesFiltered() {
      return this.companies.filter(company => {
        return company.tradingname.toLowerCase().includes(this.search.toLowerCase()) ||
          company.clientid.toLowerCase().includes(this.search.toLowerCase());
      });
    }
  },
  methods: {
    getCompany() {
      this.$store.dispatch('API_CALL', {
        $method: 'get_all_company'
      })
        .then((response) => {
          if (this.$store.state.type == 3) {
            this.companies = response.data.data.sort((a, b) => {
              if (a.tradingname < b.tradingname) {
                return -1;
              } 
              if (a.tradingname > b.tradingname) {
                return 1;
              }
              return 0;
            });
          }
        });
    },
    getCreatedCompany() {
      this.$store.dispatch('API_CALL', {
        $method: 'get_companies'
      })
        .then((response) => {
          if (this.$store.state.type == 3) {
            this.createdCompanies = response.data.data;
          }
        });
    },
    updateOptions(options) {
      this.paginationOptions = options;
    },
    // working_day and working_time are an array and an object, so handing the
    // defaults out directly lets the form's v-model write into the component's
    // own defaults: a time typed for one company then shows up on the next one
    // missing that key. Harmless while handleSave still overwrote working_time
    // with undefined; now that it does not, the leaked value would be saved.
    defaultMetadata() {
      return JSON.parse(JSON.stringify(this.defaultSelectedCompany.metadata));
    },
    handleEdit(item) {
      this.dialog_form = true;
      const selectedCompany = this.createdCompanies.find(company => company.clientid === item.clientid);
      if (selectedCompany) {
        // update_company replaces the whole metadata blob, so start from the
        // server's copy: building it from this form's default keys alone drops
        // every key the form has no field for (torus_api_url, the advance
        // booking settings, ...) the first time anyone saves here.
        this.selectedCompany = {
          clientid: selectedCompany.clientid,
          name: selectedCompany.tradingname,
          metadata: { ...selectedCompany.metadata }
        };
        const defaults = this.defaultMetadata();
        for (let key in defaults) {
          if (!this.selectedCompany.metadata.hasOwnProperty(key)) {
            this.selectedCompany.metadata[key] = defaults[key];
          }
        }
      } else {
        this.selectedCompany = {
          ...this.defaultSelectedCompany,
          metadata: this.defaultMetadata(),
          clientid: item.clientid,
          name: item.tradingname
        };
      }
      this.selectedCompany.metadata.overnight_booking = this.getValueAsBoolean(this.selectedCompany.metadata.overnight_booking);
      this.selectedCompany.metadata.auto_approve_overnight_booking = this.getValueAsBoolean(this.selectedCompany.metadata.auto_approve_overnight_booking);
      this.selectedCompany.metadata.recurring_booking = this.getValueAsBoolean(this.selectedCompany.metadata.recurring_booking);
      this.selectedCompany.metadata.late_returning_key = this.getValueAsBoolean(this.selectedCompany.metadata.late_returning_key);
      this.selectedCompany.metadata.restrict_booking = this.getValueAsBoolean(this.selectedCompany.metadata.restrict_booking);
      this.selectedCompany.metadata.no_need_rfid = this.getValueAsBoolean(this.selectedCompany.metadata.no_need_rfid);
      this.selectedCompany.metadata.no_need_destination = this.getValueAsBoolean(this.selectedCompany.metadata.no_need_destination);
      this.selectedCompany.metadata.no_need_search_address = this.getValueAsBoolean(this.selectedCompany.metadata.no_need_search_address);
      this.selectedCompany.metadata.max_booking_days = this.normaliseMaxBookingDays(this.selectedCompany.metadata.max_booking_days);
    },
    // Blank means "no limit", and the backend reads a present key as a limit —
    // so anything that is not a usable day count has to come back as "" and be
    // dropped from the payload rather than sent as 0.
    normaliseMaxBookingDays(value) {
      const days = parseInt(value, 10);
      return isNaN(days) || days < 1 ? '' : days;
    },
    handleSave() {
      this.selectedCompany.metadata.no_need_rfid = this.getValueAsBoolean(this.selectedCompany.metadata.no_need_rfid);
      this.selectedCompany.metadata.no_need_destination = this.getValueAsBoolean(this.selectedCompany.metadata.no_need_destination);
      this.selectedCompany.metadata.no_need_search_address = this.getValueAsBoolean(this.selectedCompany.metadata.no_need_search_address);
      // The time pickers write straight into metadata.working_time, so there is
      // nothing to copy in here. Assigning this.start/this.end — which this
      // component never declares — put undefined in their place, qs drops
      // undefined keys, and update_company replaces the whole blob: every save
      // silently deleted the company's working hours.
      if (!this.selectedCompany.metadata.hasOwnProperty("working_time")) {
        this.selectedCompany.metadata.working_time = { start: "", end: "" };
      }
      this.selectedCompany.metadata.restrict_booking = this.selectedCompany.metadata
        .restrict_booking
        ? 1
        : 0;
      this.selectedCompany.metadata.overnight_booking = this.selectedCompany.metadata
        .overnight_booking
        ? 1
        : 0;
      this.selectedCompany.metadata.auto_approve_overnight_booking = this.selectedCompany.metadata
        .auto_approve_overnight_booking
        ? 1
        : 0;
      this.selectedCompany.metadata.recurring_booking = this.selectedCompany.metadata
        .recurring_booking
        ? 1
        : 0;
      this.selectedCompany.metadata.late_returning_key = this.selectedCompany.metadata
        .late_returning_key
        ? 1
        : 0;
      this.selectedCompany.metadata.max_booking_days = this.normaliseMaxBookingDays(this.selectedCompany.metadata.max_booking_days);
      this.saving = true;
      const metadata = { ...this.selectedCompany.metadata };
      if (metadata.max_booking_days === '') {
        delete metadata.max_booking_days;
      }
      let payload = {
        clientid: this.selectedCompany.clientid,
        metadata,
        administrator: 1
      };
      this.$store.dispatch("UPDATE_COMPANY", payload).then(() => {
        this.dialog_form = false;
        this.saving = false;
        this.$swal({
          icon: "success",
          title: "Success"
        }).then(isConfirmed => {
          if (isConfirmed) {
            this.loadCompany();
          }
        });
      });
    },
    getValueAsBoolean(value) {
      return value == 1 || value == '1' || value == 'true' || value == true ? 1 : 0;
    },
    isLinked(clientid) {
      return this.createdCompanies.some(company => company.clientid === clientid);
    }
  },
};
</script>
